import React, { useState } from 'react';
import { CsvVariable, CsvImportPreview, CSVVarHeaders, CsvImportGroups, CsvWorkspaceGroups } from '../types';
import { useDropzone } from 'react-dropzone';
import * as Yup from 'yup';
import Papa from 'papaparse';
import { AlertTitle, AlertView, GradientTextAction, Poppins, Spacer } from 'src/common';
import { Dropzone } from 'src/components/CsvUpload';
import { ValidationErrorItem } from 'src/utils/csv';
import { generateCsvTemplateLink, getStandardVariableNames } from '../util';
import { isValidNumberValue } from 'src/utils/csv';
import { VARIABLE_NAME_REGEX } from 'src/components/formComposed/WorkspaceVariablesForm/util';

interface FormProps {
  onParsed: (preview: CsvImportPreview) => void;
  onClear: () => void;
  pendingImport: CsvImportPreview | null;
  originalVariables?: Array<{ name: string; value?: number; currency: boolean }>;
  workspaceVariableNames: string[];
  scopeType: 'workspace' | 'assessment';
}

const Form: React.FC<FormProps> = ({
  onParsed,
  onClear,
  pendingImport,
  originalVariables = [],
  workspaceVariableNames,
  scopeType,
}) => {
  const [errors, setErrors] = useState<ValidationErrorItem[]>([]);

  const standardVariableNames = getStandardVariableNames();

  const csvVariableSchema = Yup.object({
    [CSVVarHeaders.Name]: Yup.string()
      .required('Name is required')
      .test('min-length', 'Name is too short', (value) => !!value && value.trim().length >= 1)
      .matches(VARIABLE_NAME_REGEX, 'Contains unsupported characters'),
    [CSVVarHeaders.Value]: Yup.string().when(CSVVarHeaders.Name, {
      is: (name: string) => standardVariableNames.includes(name?.trim()),

      then: (schema) =>
        schema.test('is-valid-number-or-empty', 'Value must be a valid number', (value) => {
          const isValid = isValidNumberValue(value);
          return isValid;
        }),

      otherwise: (schema) =>
        schema.required('Value is required').test('is-valid-number', 'Value must be a valid number', (value) => {
          if (value === undefined || value === null || value === '') return false;
          const isValid = isValidNumberValue(value);
          return isValid;
        }),
    }),
    [CSVVarHeaders['Currency (yes/no)']]: Yup.string().when(CSVVarHeaders.Name, {
      is: (name: string) => !standardVariableNames.includes(name?.trim()),
      then: (schema) =>
        schema.required('Currency field is required').oneOf(['yes', 'no'], 'Currency must be "yes" or "no"'),
      otherwise: (schema) => schema.optional(),
    }),
  });

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (files) => {
      if (!files || files.length === 0) return;

      onClear();
      setErrors([]);

      const file = files[0];

      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          const { data, errors: parseErrors } = results;

          const criticalErrors = parseErrors.filter(
            (err) => err.type !== 'FieldMismatch' && err.code !== 'TooFewFields' && err.code !== 'TooManyFields',
          );

          if (criticalErrors.length > 0) {
            setErrors([
              {
                rowIndex: 0,
                errors: [
                  {
                    path: 'File',
                    message: `CSV parsing error: ${criticalErrors[0].message}`,
                  },
                ],
              },
            ]);
            return;
          }

          const validationErrors: ValidationErrorItem[] = [];
          const validVariables: CsvVariable[] = [];

          data.forEach((row: unknown, index: number) => {
            const rowData = row as Record<string, string>;

            try {
              csvVariableSchema.validateSync(rowData, { abortEarly: false });

              const variableName = rowData[CSVVarHeaders.Name].trim();
              const originalVariable = originalVariables.find((v) => v.name === variableName);
              const isStandardVariable = standardVariableNames.includes(variableName);

              let currencyValue = false;
              if (isStandardVariable && originalVariable) {
                currencyValue = originalVariable.currency;
              } else {
                currencyValue = rowData[CSVVarHeaders['Currency (yes/no)']] === 'yes';
              }

              const rawValue = rowData[CSVVarHeaders.Value];
              const parsedValue = rawValue === '' || rawValue === undefined ? undefined : Number(rawValue);

              validVariables.push({
                name: variableName,
                value: parsedValue,
                currency: currencyValue,
              });
            } catch (validationError) {
              if (validationError instanceof Yup.ValidationError) {
                validationErrors.push({
                  rowIndex: index,
                  errors: validationError.inner.map((err) => ({
                    path: err.path || 'Unknown',
                    message: err.message,
                  })),
                });
              }
            }
          });

          if (validationErrors.length > 0) {
            setErrors(validationErrors);
          } else {
            setErrors([]);

            let toUpdate = 0;
            let toCreate = 0;
            let toOverride = 0;
            let unchanged = 0;
            const changedVariables: CsvVariable[] = [];

            const groups: CsvImportGroups = {
              customAssessmentVars: { variables: [], action: 'include' },
              workspaceVars: { variables: [], action: 'include' },
              overriddenVars: { variables: [], action: 'include' },
              newVars: { variables: [], action: 'include' },
            };

            const workspaceGroups: CsvWorkspaceGroups = {
              existingVars: { variables: [], action: 'include' },
              newVars: { variables: [], action: 'include' },
            };

            validVariables.forEach((csvVar) => {
              const originalVar = originalVariables.find((v) => v.name === csvVar.name);
              const existsInOriginal = !!originalVar;
              const existsInWorkspace = workspaceVariableNames.includes(csvVar.name);

              const originalValue = originalVar?.value;
              const csvValue = csvVar.value;

              const isValueSame =
                originalVar && (originalValue === csvValue || (originalValue == null && csvValue == null));
              const isCurrencySame = originalVar && originalVar.currency === csvVar.currency;
              const isUnchanged = isValueSame && isCurrencySame;

              if (scopeType === 'assessment') {
                if (existsInOriginal && existsInWorkspace) {
                  if (isUnchanged) {
                    unchanged++;
                  } else {
                    toUpdate++;
                    changedVariables.push(csvVar);
                    groups.overriddenVars.variables.push(csvVar);
                  }
                } else if (existsInOriginal && !existsInWorkspace) {
                  if (isUnchanged) {
                    unchanged++;
                  } else {
                    toUpdate++;
                    changedVariables.push(csvVar);
                    groups.customAssessmentVars.variables.push(csvVar);
                  }
                } else if (!existsInOriginal && existsInWorkspace) {
                  toOverride++;
                  changedVariables.push(csvVar);
                  groups.workspaceVars.variables.push(csvVar);
                } else {
                  toCreate++;
                  changedVariables.push(csvVar);
                  groups.newVars.variables.push(csvVar);
                }
              } else {
                if (existsInOriginal) {
                  if (isUnchanged) {
                    unchanged++;
                  } else {
                    toUpdate++;
                    changedVariables.push(csvVar);
                    workspaceGroups.existingVars.variables.push(csvVar);
                  }
                } else {
                  toCreate++;
                  changedVariables.push(csvVar);
                  workspaceGroups.newVars.variables.push(csvVar);
                }
              }
            });

            const preview: CsvImportPreview = {
              variables: changedVariables,
              fileName: file.name,
              summary: {
                total: changedVariables.length,
                toUpdate,
                toCreate,
                toOverride,
              },
              groups: scopeType === 'assessment' ? groups : undefined,
              workspaceGroups: scopeType === 'workspace' ? workspaceGroups : undefined,
            };

            onParsed(preview);
          }
        },
        error: (error) => {
          setErrors([
            {
              rowIndex: 0,
              errors: [
                {
                  path: 'File',
                  message: `CSV parsing error: ${error.message}`,
                },
              ],
            },
          ]);
        },
      });
    },
    multiple: false,
    accept: {
      'text/csv': ['.csv'],
    },
  });

  return (
    <>
      <Dropzone {...getRootProps()}>
        <div style={{ position: 'absolute', right: 8, top: 8 }}>
          <a
            href={generateCsvTemplateLink()}
            download="variables_template.csv"
            style={{ textDecoration: 'none' }}
            onClick={(e) => e.stopPropagation()}
          >
            <GradientTextAction $underline weight={500} onClick={() => {}} px={14}>
              Download CSV Template
            </GradientTextAction>
          </a>
        </div>
        <input {...getInputProps()} />
        {pendingImport ? (
          <Poppins px={14}>File selected: {pendingImport.fileName}</Poppins>
        ) : (
          <Poppins px={14} weight={500}>
            {isDragActive ? 'Drop the file here ...' : 'Drag and drop a file here, or click to select file'}
          </Poppins>
        )}
      </Dropzone>

      {errors.length > 0 && (
        <>
          <Spacer $px={16} />
          <AlertView>
            {errors.map((el) => (
              <AlertTitle key={JSON.stringify(el.errors)}>
                <div style={{ fontStyle: 'italic' }}>
                  <Poppins color="error" px={16} weight={600}>
                    Error in row {el.rowIndex + 1}:
                  </Poppins>
                  <div />
                  {el.errors.map((e, idx) => (
                    <Poppins px={14} color="error" css="display: block" key={idx}>
                      {`${e.message}`}
                    </Poppins>
                  ))}
                </div>
              </AlertTitle>
            ))}
          </AlertView>
        </>
      )}
    </>
  );
};

export default Form;
